/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Shape accepted by the `POST /commerce-assistant/chat` API Management operation.
 */
export interface IAssistantChatRequest {
    message: string;
    previous_response_id?: string;
}

export interface IAssistantContentPart {
    text?: string;
}

export interface IAssistantOutputItem {
    type?: string;
    role?: string;
    content?: IAssistantContentPart[] | string;
}

/**
 * Shape returned by the API Management operation once it has relayed the Foundry response.
 */
export interface IAssistantChatResponse {
    id?: string;
    output?: IAssistantOutputItem[];
    error?: { message?: string };
}

export interface IAssistantReply {
    responseId?: string;
    text: string;
}

export interface IApimAssistantClientOptions {
    /**
     * Absolute HTTPS URL of the API Management chat operation.
     */
    endpointUrl: string;

    /**
     * Commerce or Entra token forwarded to API Management for caller validation.
     */
    authorizationToken?: string;

    /**
     * Scheme prefixed to the token, for example `id_token` or `bearer`.
     */
    tokenScheme?: string;
}

const MAX_MESSAGE_LENGTH: number = 4000;

/**
 * Validates that the configured gateway URL is an absolute HTTPS URL.
 * Tokens are only ever sent to an endpoint that passes this check.
 * @param endpointUrl - Configured API Management endpoint.
 * @returns True when the endpoint is safe to call.
 */
export const isSecureEndpoint = (endpointUrl?: string): boolean => {
    if (!endpointUrl) {
        return false;
    }

    try {
        return new URL(endpointUrl).protocol.toLowerCase() === 'https:';
    } catch {
        return false;
    }
};

/**
 * Flattens the Foundry style `output` array into the assistant reply text.
 * @param payload - Parsed gateway response.
 * @returns The assistant text, or an empty string when nothing usable was returned.
 */
export const extractReplyText = (payload: IAssistantChatResponse): string => {
    const outputItems = payload.output ?? [];
    const texts: string[] = [];

    for (const item of outputItems) {
        if (item.role && item.role !== 'assistant') {
            continue;
        }

        if (typeof item.content === 'string') {
            texts.push(item.content);
            continue;
        }

        for (const part of item.content ?? []) {
            if (part.text) {
                texts.push(part.text);
            }
        }
    }

    return texts.join('\n\n').trim();
};

/**
 * Sends a single shopper turn to the API Management assistant gateway.
 * API Management owns the Foundry credentials and backend routing, so the storefront
 * only needs the gateway URL and, optionally, the signed-in shopper's token.
 * @param options - Gateway endpoint and caller token.
 * @param message - Shopper prompt.
 * @param previousResponseId - Identifier returned by the previous turn, used to continue the conversation.
 * @returns The assistant reply plus the response identifier for the next turn.
 */
export const sendAssistantMessageAsync = async (
    options: IApimAssistantClientOptions,
    message: string,
    previousResponseId?: string
): Promise<IAssistantReply> => {
    if (!isSecureEndpoint(options.endpointUrl)) {
        throw new Error('The commerce assistant endpoint must be an absolute HTTPS URL.');
    }

    const body: IAssistantChatRequest = { message: message.slice(0, MAX_MESSAGE_LENGTH) };

    if (previousResponseId) {
        body.previous_response_id = previousResponseId;
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (options.authorizationToken) {
        headers.Authorization = `${options.tokenScheme ?? 'bearer'} ${options.authorizationToken}`;
    }

    const httpResponse = await fetch(options.endpointUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
    });

    const responseText = await httpResponse.text();
    let payload: IAssistantChatResponse | undefined;

    if (responseText) {
        try {
            payload = JSON.parse(responseText) as IAssistantChatResponse;
        } catch {
            payload = undefined;
        }
    }

    if (!httpResponse.ok) {
        const gatewayMessage = payload?.error?.message;
        throw new Error(gatewayMessage ?? `The assistant gateway returned ${httpResponse.status} ${httpResponse.statusText}.`);
    }

    if (!payload) {
        throw new Error('The assistant gateway returned an empty or invalid response.');
    }

    return {
        responseId: payload.id,
        text: extractReplyText(payload)
    };
};
